import React from 'react';
import { telHref, formatPhone } from '../shared/format';
import InfoTip from '../shared/InfoTip';
import { BTN_PRIMARY, CARD_LIST, CARD_TITLE } from './liStyles';

// Help right now — the seven urgent needs (urgent care, vet, RV repair, towing,
// propane, pharmacy…), permanently beside the Ask console because an emergency
// cannot wait for a scroll. Each row: the nearest place and how far, and — when the
// nearest is not open — the nearest one that is, with a Call button.
// Tapping the need asks for it in the console; tapping a place opens it.
const PRIORITY_CATEGORIES = [
  /urgent care|urgent|\bER\b/i,
  /vet|animal hospital/i,
  /rv repair/i,
  /towing/i,
];

/* ─── Category icons ─────────────────────────────────────────────────
   Inline stroke glyphs (Feather/Lucide style, matching the repo's other
   inline icon components — no icon library is installed). One per urgent
   category, matched by regex against the row's label + need_key (same
   pattern as PRIORITY_CATEGORIES). Falls back to a map-pin for anything
   without a dedicated glyph. */
const Glyph = ({ children, className = '' }) => (
  <svg className={`w-4 h-4 shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const CrossIcon = () => <Glyph><path d="M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z" /></Glyph>;
const PawIcon = () => <Glyph><circle cx="6" cy="10" r="1.5" /><circle cx="10" cy="6" r="1.5" /><circle cx="14" cy="6" r="1.5" /><circle cx="18" cy="10" r="1.5" /><path d="M12 11c-2.8 0-5 1.8-5 4 0 1.7 1.8 3 5 3s5-1.3 5-3c0-2.2-2.2-4-5-4z" /></Glyph>;
const WrenchIcon = () => <Glyph><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></Glyph>;
const TruckIcon = () => <Glyph><path d="M5 18a2 2 0 1 0 4 0a2 2 0 1 0-4 0" /><path d="M15 18a2 2 0 1 0 4 0a2 2 0 1 0-4 0" /><path d="M2 9V6a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /><path d="M14 9h4l3 3v4a1 1 0 0 1-1 1h-2" /></Glyph>;
const FlameIcon = () => <Glyph><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" /></Glyph>;
const PillIcon = () => <Glyph><path d="M10.5 20.5 20 11a4.95 4.95 0 1 0-7-7l-9.5 9.5a4.95 4.95 0 1 0 7 7z" /><path d="m8.5 8.5 7 7" /></Glyph>;
const TireIcon = () => <Glyph><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.5" /></Glyph>;
const PinIcon = () => <Glyph><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></Glyph>;

// Item 2 (work-page-fixes) — per-category icon colors. Each urgent category's
// glyph takes a color matching its meaning (red cross, amber paw, blue wrench,
// yellow tow, orange flame, green pill, gray tire); the color sits on the icon
// only — category label, place name, phone number and button text stay
// white/muted. Tailwind's standard shades (emerald/green, amber, orange, red,
// yellow) are the codebase's color tokens; brand blue is #32B1FF. The fallback
// (PinIcon) keeps brand blue.
const CATEGORY_ICONS = [
  { test: /urgent care|urgent|\bER\b|emergency/i, Icon: CrossIcon, color: 'text-red-500 dark:text-red-400' },
  { test: /vet|animal/i, Icon: PawIcon, color: 'text-amber-500 dark:text-amber-400' },
  { test: /rv repair|rv/i, Icon: WrenchIcon, color: 'text-[#32B1FF]' },
  { test: /tow/i, Icon: TruckIcon, color: 'text-yellow-500 dark:text-yellow-400' },
  { test: /propane|gas/i, Icon: FlameIcon, color: 'text-orange-500 dark:text-orange-400' },
  { test: /pharmac|drug|prescription/i, Icon: PillIcon, color: 'text-emerald-500 dark:text-emerald-400' },
  { test: /tire|wheel/i, Icon: TireIcon, color: 'text-gray-400 dark:text-gray-500' },
];
const FALLBACK_ICON_COLOR = 'text-[#32B1FF]';
const iconFor = (row) => {
  const hay = `${row.label || ''} ${row.need_key || ''}`;
  const match = CATEGORY_ICONS.find((c) => c.test.test(hay));
  const Icon = match ? match.Icon : PinIcon;
  const color = match ? match.color : FALLBACK_ICON_COLOR;
  return { Icon, color };
};

const HelpRail = ({ panel, onAskNeed, onOpenDrill }) => {
  const rows = Array.isArray(panel?.facts?.rows) ? panel.facts.rows : [];

  // Rank each row by its priority category (lower = sooner). Rows whose label or
  // need_key matches no priority pattern sink to the bottom in their original order
  // (the index tiebreaker keeps the sort stable).
  const rankOf = (row) => {
    const hay = `${row.label || ''} ${row.need_key || ''}`;
    const idx = PRIORITY_CATEGORIES.findIndex((re) => re.test(hay));
    return idx === -1 ? PRIORITY_CATEGORIES.length : idx;
  };
  const ordered = rows
    .map((row, i) => ({ row, i, rank: rankOf(row) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.row);

  // Item 3 — all services show. No expander, no truncation: every category the API
  // returns renders as a card (the 2-column grid wraps to as many rows as needed).
  return (
    <section id="panel-triage" className={CARD_LIST} aria-label="Essential Services Nearby">
      <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
        <h2 className={`${CARD_TITLE} text-lg`}>
          Essential Services Nearby
          <InfoTip text="The nearest place for each urgent need, and the nearest one open right now." />
        </h2>
        {/* Item 2 — subtext below the title. Same small muted styling as the RightNow
            sublabel ("N places within a 20-minute drive are open right now."). */}
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-2">Nearest open places when a guest needs urgent help.</p>
      </div>
      {/* Item 1 — card grid. Each category is a self-contained mini business card
          (icon + label, place name, distance badge + hours, phone number, Call
          button). On mobile (<640px) the grid collapses to 1 column so each card
          takes the full width — giving the category label, phone number and Call
          button enough room without truncation or mid-number wrapping. At sm+
          it returns to 2 columns to fill the panel's horizontal space. */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 sm:p-4">
        {ordered.map((row) => {
          const open = row.nearest_open;
          const tel = telHref(open?.phone);
          const { Icon: CategoryIcon, color: iconColor } = iconFor(row);
          return (
            <li
              key={row.need_key}
              role={open ? 'button' : undefined}
              tabIndex={open ? 0 : undefined}
              onClick={open ? () => onOpenDrill(open.drill) : undefined}
              onKeyDown={open ? (e) => { if (e.key === 'Enter') onOpenDrill(open.drill); } : undefined}
              className={`rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex flex-col gap-2 text-left transition-colors shadow-sm dark:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50 ${
                open ? 'cursor-pointer hover:border-[#32B1FF]/50' : ''
              }`}
            >
              {/* work-page-fixes Item 2 — reverted to the transparent/bordered
                  treatment. The solid fill (bg-white dark:bg-gray-800) is removed so
                  the panel background shows through; the border (border-gray-200
                  dark:border-gray-700) is kept as the card's defining edge. Only the
                  card background changes — the colored category icons (red urgent
                  care, amber vet, blue RV repair, yellow tow, orange flame, green
                  pill, gray tire) sit on the icon span via currentColor and are
                  unaffected, as is the card content (place name, chips, phone, Call). */}
              {/* Top line: category icon + label. The label asks in the console;
                  stopPropagation so it only asks and doesn't also fire the card's
                  drill navigation. */}
              <div className="flex items-center gap-2 min-w-0">
                <span className={`${iconColor} shrink-0`}><CategoryIcon /></span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onAskNeed(row); }}
                  className="text-left text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-[#32B1FF] focus:outline-none focus-visible:underline min-w-0 truncate"
                >
                  {row.label}
                </button>
              </div>
              {open ? (
                <>
                  {/* Place name from nearest_open — green/success text signals it is
                      open right now. Plain text: the whole card opens the place
                      detail drill, so no nested button is needed. */}
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 min-w-0">
                    {open.name}
                  </p>
                  {/* Meta chips matching the Nearby Places answer card (AnswerCard.js):
                      one chip for hours ("Open until HH:MM PM" from hours_label) and
                      one for drive ("X min drive" from drive_minutes). Same chip
                      style as the answer cards. Drive time appears once — here only. */}
                  <div className="flex flex-wrap gap-1.5">
                    {open.hours_label && (
                      <span className="text-sm px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap">{open.hours_label}</span>
                    )}
                    {open.drive_minutes != null && (
                      <span className="text-sm px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap">{open.drive_minutes} min drive</span>
                    )}
                  </div>
                  {/* Phone number (visible text, left) and Call button (right) on one
                      row. The row stops propagation so clicking either only dials and
                      doesn't fire the card's drill navigation. */}
                  <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation" className="flex items-center justify-between gap-2">
                    {open.phone && (
                      <a href={tel} className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-[#32B1FF] whitespace-nowrap">
                        {formatPhone(open.phone)}
                      </a>
                    )}
                    {tel && <a href={tel} className={`${BTN_PRIMARY} !px-2.5 !py-1 shrink-0`}>Call</a>}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No place open nearby right now.</p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default HelpRail;
